class SectionsRandomizer {

    static generateRuleIndex(randomizedSections) {
        return Math.floor(Math.random() * Object.keys(randomizedSections[0].randomizedRules).length);
    }

    static generateSections(randomizedSections, start, end, activeRulesPath, totalNumberOfSections) {
        var sections = [];
        let previousLanes = []; // Store lanes from the previous section
        const allPossibleLanes = this.generateAllPossibleLanes();

        let previousIsColorRule = null;
        let previousRuleCharacterWithSpeed3 = null;

        let remainingSections = totalNumberOfSections;
        let newTotalNumberOfSections = 0;

        for (const [randomConfigIndex, randomConfig] of randomizedSections.entries()) {

            // Calculate the number of sections for this configuration based on the ranges and the configured total number of sections
            const minRange = randomConfig.rangeOfSections[0];
            const maxRange = randomConfig.rangeOfSections[1];
            const remainingConfigs = randomizedSections.length - randomConfigIndex - 1;
        
            // Ensure we stay within the defined range
            let minPossible = Math.max(minRange, remainingSections - remainingConfigs * maxRange);
            let maxPossible = Math.min(maxRange, remainingSections - remainingConfigs * minRange);
        
            // If minPossible is greater than maxRange, set it to maxRange
            if (minPossible > maxRange) minPossible = maxRange;
        
            // If maxPossible is less than minRange, set it to minRange
            if (maxPossible < minRange) maxPossible = minRange;
        
            const numSections = Math.floor(Math.random() * (maxPossible - minPossible + 1)) + minPossible;
            remainingSections -= numSections;
            newTotalNumberOfSections += numSections;
            
            var speeds = [0, 1, 2, 3];
            var colors = [1, 2, 3, 4];
            var shapes = ["a", "b", "c", "d"];

            this.shuffleArray(speeds);
            this.shuffleArray(colors);
            this.shuffleArray(shapes);

            var ruleCharacters;
            var isColorRule = randomConfig.randomizedRules[activeRulesPath] === "color";
            if (isColorRule) {
                ruleCharacters = colors;
            } else {
                ruleCharacters = shapes;
            }

            // Link a rule character to a speed for this set of sections
            var ruleToSpeed = speeds.map((speed, index) => ({
                speed: speed,
                ruleCharacter: ruleCharacters[index]
            }));

            // [Requirement] Ensure that the character with the max speed does not repeat in the next section
            if (previousIsColorRule === isColorRule && previousRuleCharacterWithSpeed3 !== null && ruleToSpeed.find(a => a.ruleCharacter === previousRuleCharacterWithSpeed3).speed === 3) {
                let randomSpeed = Math.floor(Math.random() * 2);
                let itemWithSpeed3 = ruleToSpeed.find(a => a.ruleCharacter === previousRuleCharacterWithSpeed3);
                let itemWithOtherSpeed = ruleToSpeed.find(a => a.speed === randomSpeed);
                itemWithSpeed3.ruleCharacter = itemWithOtherSpeed.ruleCharacter
                itemWithOtherSpeed.ruleCharacter = previousRuleCharacterWithSpeed3;
            }

            for (let i = 0; i < numSections; i++) {
                this.shuffleArray(ruleToSpeed, true);
                var availableLanes = allPossibleLanes.filter(lane => !previousLanes.includes(lane));
                this.shuffleArray(availableLanes);

                var lanes = [];
                var success = this.selectLanes(ruleToSpeed, availableLanes, lanes, []);

                if (!success) {
                    throw new Error("Failed to generate valid lanes");
                }

                // [Requirement] Ensure that none of the lanes of the new chunk have characters in the same position as the previous chunk
                if (this.hasSamePositionCharacters(lanes, previousLanes)) {
                    i--; // Retry the current iteration
                    continue;
                }

                // [Requirement] Ensure the previous rule character with speed 3 is not in the same position as the new rule character with speed 3
                if (i === 0 && previousRuleCharacterWithSpeed3 !== null) {
                    const newRuleCharacterWithSpeed3 = ruleToSpeed.find(a => a.speed === 3).ruleCharacter;
                    const previousIndex = previousLanes.findIndex(lane => lane.includes(previousRuleCharacterWithSpeed3));
                    const newIndex = lanes.findIndex(lane => lane.includes(newRuleCharacterWithSpeed3));

                    if (previousIndex === newIndex) {
                        i--; // Retry the current iteration
                        continue;
                    }
                }

                let ruleCharacterWithSpeed3 = ruleToSpeed.find(a => a.speed === 3).ruleCharacter;

                // [Requirement] Ensure that the first section generated of the whole track does not have the winning lane as index 1 (starting lane after tutorial)
                if (sections.length === 0 && lanes[1].includes(ruleCharacterWithSpeed3)) {
                    i--; // Retry the current iteration
                    continue;
                }

                sections.push({
                    speeds: ruleToSpeed.map(a => a.speed),
                    lanes: lanes.join(','),
                    action: i === 0 ? 'rulechange' : undefined // Optional action for the first section
                });

                previousLanes = [...lanes]; // Update previous lanes to avoid repetition in the next section
                previousIsColorRule = isColorRule;
                previousRuleCharacterWithSpeed3 = ruleCharacterWithSpeed3;
            }
        }
        
        if(totalNumberOfSections != newTotalNumberOfSections) {
            console.warn(`The total number of sections generated (${newTotalNumberOfSections}) does not match the configured total number of sections (${totalNumberOfSections}).`);
        }

        if (sections.length > 0) {
            sections[sections.length - 1].action = "pre-finish";
            sections[sections.length - 1].marker = { "skin": "FinishTile", "position": -30 };
        }

        if (start && start.length > 0) {
            sections = [...start, ...sections];
        }

        if (end && end.length > 0) {
            sections = [...sections, ...end];
        }
        return sections;
    }

    static selectLanes(ruleToSpeed, availableLanes, lanes, charactersToIgnore) {
        if (ruleToSpeed.length === 0) {
            return true;
        }

        const { speed, ruleCharacter } = ruleToSpeed[0];
        for (let i = 0; i < availableLanes.length; i++) {
            const matchedLane = availableLanes[i];
            if (matchedLane.includes(ruleCharacter) && !charactersToIgnore.some(char => matchedLane.includes(char))) {
                lanes.push(matchedLane);
                const charactersToRemove = matchedLane.split('');
                const newAvailableLanes = availableLanes.filter(lane =>
                    !charactersToRemove.some(char => lane.includes(char))
                );

                if (this.selectLanes(ruleToSpeed.slice(1), newAvailableLanes, lanes, [...charactersToIgnore, ...charactersToRemove])) {
                    return true;
                }

                // Backtrack in case the this matched lane doesn't lead to a valid solution
                lanes.pop();
            }
        }
        return false;
    }

    // Check if the characters are in the same position as in the previous lanes
    static samePositionAsPrevious(lanes, previousLanes, colorRule = true) {
        var regex = /[0-9]/g;
        if (colorRule) {
            regex = /[a-z]+/g;
        }
        if (previousLanes.length === 0) {
            return false;
        }
        for (let i = 0; i < lanes.length; i++) {
            const currentCharacters = lanes[i].replace(regex, '');
            const previousCharacters = previousLanes[i].replace(regex, '');
            if (currentCharacters === previousCharacters) {
                return true;
            }
        }
        return false;
    }

    // Helper function to shuffle an array
    static shuffleArray(array, ensureNewValue = false) {
        const clonedArray = [...array];
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]]; // Swap elements
        }
        if (ensureNewValue && this.arraysAreEqual(array, clonedArray)) {
            this.shuffleArray(array, ensureNewValue);
        }
    }

    static arraysAreEqual(arr1, arr2) {
        return arr1.length === arr2.length && arr1.every((value, index) => value === arr2[index]);
    }

    // Generates all possible lanes from 1a to 4d
    static generateAllPossibleLanes() {
        const colors = [1, 2, 3, 4];
        const shapes = ['a', 'b', 'c', 'd'];
        const allLanes = [];

        for (let color of colors) {
            for (let shape of shapes) {
                allLanes.push(`${color}${shape}`);
            }
        }
        return allLanes;
    }

    // Check if the lanes of the new chunk have characters in the same positions as the lanes in the previous chunk
    static hasSamePositionCharacters(newLanes, previousLanes) {
        if (previousLanes.length === 0) {
            return false;
        }
        for (let i = 0; i < newLanes.length; i++) {
            const newLaneCharacters = newLanes[i].split('');
            const previousLaneCharacters = previousLanes[i].split('');
            for (let j = 0; j < newLaneCharacters.length; j++) {
                if (newLaneCharacters[j] === previousLaneCharacters[j]) {
                    return true;
                }
            }
        }
        return false;
    }
}

module.exports = SectionsRandomizer;